import React, { useState } from 'react';
import { useToast } from "../hooks/use-toast";
import { NetworkAxiosInstance } from "../Network/network";
import { isViewer } from "../interface/RoleHelpers";

const TEMPLATE_URL = 'networks/template';
const EXPORT_ALL_URL = 'networks/export';
const MAX_SIZE = 10 * 1024 * 1024;

interface FileUploadProps {
  fetchItems?: () => Promise<void>;
  totalItems?: number;
  hasFilters?: boolean;
  exportParams?: Record<string, any>;
}

const saveBlobResponse = (response: any, fallbackName: string) => {
  const disposition: string = response.headers?.['content-disposition'] ?? '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  const filename = match ? decodeURIComponent(match[1]) : fallbackName;

  const blob = new Blob([response.data], {
    type: response.headers?.['content-type'] || 'application/octet-stream',
  });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

const getBlobErrorMessage = async (error: any, fallback: string): Promise<string> => {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.error?.message || parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return data?.error?.message || data?.message || error?.message || fallback;
};

const FileUpload: React.FC<FileUploadProps> = ({
  fetchItems,
  totalItems = 0,
  hasFilters = false,
  exportParams = {},
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { toast } = useToast();

  const resetInput = () => {
    const input = document.getElementById('file') as HTMLInputElement;
    if (input) input.value = '';
  };

  const formatSize = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${Math.max(1, Math.round(bytes / 1024))} KB`
      : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

  const validateAndSet = (f: File | undefined | null) => {
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      toast({
        title: "Unsupported file",
        description: "Please select an Excel file (.xlsx) based on the template.",
        variant: "destructive",
      });
      return;
    }
    if (f.size > MAX_SIZE) {
      toast({
        title: "File too large",
        description: "The file must be 10 MB or smaller.",
        variant: "destructive",
      });
      return;
    }
    setFile(f);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    validateAndSet(e.dataTransfer.files?.[0]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    validateAndSet(e.target.files?.[0]);
    e.target.value = '';
  };

  const handleDownloadTemplate = async () => {
    try {
      setIsDownloadingTemplate(true);
      const response = await NetworkAxiosInstance.get(TEMPLATE_URL, { responseType: 'blob' });
      saveBlobResponse(response, 'Network_Template.xlsx');
      toast({
        title: "Success",
        description: "Template downloaded successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: await getBlobErrorMessage(error, "Failed to download template."),
        variant: "destructive",
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleExportAll = async () => {
    if (isViewer()) {
      toast({
        title: "Error",
        description: "Access denied. Viewers are only allowed to view the system",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsExporting(true);
      const response = await NetworkAxiosInstance.get(EXPORT_ALL_URL, {
        params: exportParams,
        responseType: 'blob',
      });
      saveBlobResponse(response, 'Networks_Export.xlsx');
      toast({
        variant: "success",
        title: "Export Finished",
        description: hasFilters ? "Filtered networks exported successfully." : "All networks exported successfully.",
      });
    } catch (error: any) {
      toast({
        title: "Export Failed",
        description: await getBlobErrorMessage(error, "Failed to export networks."),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        title: "Error",
        description: "Please select a file to upload",
        variant: "destructive",
      });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setIsUploading(true);
      const { data: responseBody } = await NetworkAxiosInstance.post('networks/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { success, message, data } = responseBody;

      if (success) {
        const failed: any[] = data?.failed ?? [];
        const importedCount: number = data?.summary?.successful ?? data?.success?.length ?? 0;

        if (importedCount > 0 || failed.length === 0) {
          toast({
            title: "Import Finished",
            description: message || "File processed successfully.",
          });
        }

        if (fetchItems) await fetchItems();

        if (failed.length > 0) {
          const errorMessage = failed
            .map((item: any) => `Row ${item.row}: ${item.error}`)
            .join('\n');

          toast({
            title: importedCount > 0 ? `Errors in ${failed.length} Rows` : "No rows were imported",
            description: (
              <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-xs">
                {errorMessage}
              </div>
            ),
            variant: "destructive",
          });
        }

        setFile(null);
        resetInput();
      } else {
        toast({
          title: "Failed to Upload File",
          description: message || "Failed to upload file",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        "Failed to upload file";

      toast({
        title: "Import Failed",
        description: apiMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const clearFile = () => {
    setFile(null);
    resetInput();
  };

  const ready = !!file && !isUploading;

  const actionBtn =
    `fu-tpl group h-12 inline-flex items-center gap-3 pl-2 pr-5 rounded-full bg-white
     border border-slate-200 text-left shadow-sm transition-all duration-200 cursor-pointer
     hover:shadow-md hover:-translate-y-px active:translate-y-0 active:shadow-sm
     focus:outline-none focus-visible:ring-2
     disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-sm`;

  return (
    <div className="flex flex-wrap items-center justify-end gap-3" data-testid="FileUpload-root">
      <style>{`
        @keyframes fu-shimmer { 0% { transform: translateX(-120%) skewX(-20deg); } 100% { transform: translateX(260%) skewX(-20deg); } }
        @keyframes fu-pop { 0% { transform: scale(.94); } 60% { transform: scale(1.04); } 100% { transform: scale(1); } }
        @keyframes fu-nudge { 0%,100% { transform: translateY(0); } 50% { transform: translateY(2px); } }
        .fu-ready { animation: fu-pop .35s ease-out; }
        .fu-ready .fu-sheen { animation: fu-shimmer 2.6s ease-in-out .4s infinite; }
        .fu-tpl:hover:not(:disabled) .fu-arrow { animation: fu-nudge .8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .fu-ready, .fu-ready .fu-sheen, .fu-tpl:hover .fu-arrow { animation: none; } }
      `}</style>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleDownloadTemplate}
          disabled={isDownloadingTemplate}
          title="Download the Excel import template"
          data-testid="FileUpload-btn-template"
          className={`${actionBtn} hover:border-emerald-400 focus-visible:ring-emerald-400/60 disabled:cursor-wait`}
        >
          <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-inner">
            {isDownloadingTemplate ? (
              <i className="fa-solid fa-spinner fa-spin text-sm"></i>
            ) : (
              <>
                <i className="fa-solid fa-file-excel text-sm transition-opacity duration-150 group-hover:opacity-0"></i>
                <i className="fu-arrow fa-solid fa-arrow-down text-sm absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100"></i>
              </>
            )}
          </span>
          <span className="leading-tight">
            <span className="block text-sm font-bold text-[#1e3a8a]">
              {isDownloadingTemplate ? "Downloading..." : "Template"}
            </span>
            <span className="block text-[11px] font-medium text-slate-500">Excel (.xlsx)</span>
          </span>
        </button>

        <button
          type="button"
          onClick={handleExportAll}
          disabled={isExporting || totalItems === 0}
          title={totalItems === 0 ? "Nothing to export" : hasFilters ? "Export the filtered networks" : "Export all networks"}
          data-testid="FileUpload-btn-export"
          className={`${actionBtn} hover:border-[#1e3a8a]/60 focus-visible:ring-blue-400/60 disabled:cursor-wait`}
        >
          <span className="relative w-9 h-9 rounded-full bg-gradient-to-br from-[#3b5bdb] to-[#1e3a8a] text-white flex items-center justify-center shadow-inner">
            {isExporting ? (
              <i className="fa-solid fa-spinner fa-spin text-sm"></i>
            ) : (
              <>
                <i className="fa-solid fa-file-export text-sm transition-opacity duration-150 group-hover:opacity-0"></i>
                <i className="fu-arrow fa-solid fa-arrow-down text-sm absolute opacity-0 transition-opacity duration-150 group-hover:opacity-100"></i>
              </>
            )}
          </span>
          <span className="leading-tight">
            <span className="block text-sm font-bold text-[#1e3a8a]">
              {isExporting ? "Exporting..." : "Export"}
            </span>
            <span className="block text-[11px] font-medium text-slate-500">
              {hasFilters ? `${totalItems} filtered` : `All ${totalItems}`}
            </span>
          </span>
        </button>
      </div>

      <span className="hidden md:block h-6 w-px bg-slate-300/70" aria-hidden="true"></span>

      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        data-testid="FileUpload-container"
        className={`h-12 inline-flex items-center gap-2 pl-2 pr-2 rounded-full border transition-all duration-200 focus-within:ring-2 focus-within:ring-blue-400/60 ${
          isDragging
            ? "bg-[#274bb0] border-dashed border-white/80 ring-4 ring-blue-300/40 scale-[1.02]"
            : "bg-gradient-to-b from-[#24449a] to-[#1e3a8a] border-[#1e3a8a]"
        }`}
        style={{ minWidth: "280px", boxShadow: "0 4px 14px rgba(30, 58, 138, 0.28), inset 0 1px 0 rgba(255,255,255,0.14)" }}
      >
        <input
          id="file"
          type="file"
          accept=".xlsx"
          className="hidden"
          onChange={handleFileChange}
          data-testid="FileUpload-input"
        />

        <label
          htmlFor="file"
          title={file ? file.name : "Choose or drop an .xlsx file"}
          className="flex-1 min-w-0 h-9 flex items-center gap-2.5 cursor-pointer select-none m-0"
          data-testid="FileUpload-label"
        >
          <span className={`w-9 h-9 shrink-0 rounded-full flex items-center justify-center text-white transition-colors duration-200 ${
            file ? "bg-emerald-500" : "bg-white/15"
          }`}>
            <i className={`fa-solid ${isDragging ? "fa-arrow-down" : file ? "fa-file-excel" : "fa-cloud-arrow-up"} text-sm`}></i>
          </span>
          <span className="min-w-0 leading-tight">
            <span className="block truncate text-sm font-semibold text-white max-w-[150px]">
              {isDragging ? "Drop to select" : file ? file.name : "Choose file..."}
            </span>
            <span className="block text-[11px] text-white/60 truncate">
              {file ? formatSize(file.size) : "or drag & drop here"}
            </span>
          </span>
        </label>

        {file && !isUploading && (
          <button
            type="button"
            onClick={clearFile}
            title="Remove file"
            data-testid="FileUpload-btn-clear"
            className="w-6 h-6 shrink-0 rounded-full bg-white/15 text-white flex items-center justify-center text-xs transition-colors duration-200 cursor-pointer hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        )}

        <button
          type="button"
          onClick={handleUpload}
          disabled={!ready}
          data-testid="FileUpload-btn-submit"
          className={`relative overflow-hidden h-9 px-5 shrink-0 rounded-full text-sm font-bold inline-flex items-center gap-1.5 transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 ${
            ready
              ? "fu-ready bg-white text-[#1e3a8a] shadow-md cursor-pointer hover:bg-blue-50"
              : "bg-white/15 text-white/50 cursor-not-allowed"
          }`}
        >
          {ready && (
            <span
              className="fu-sheen pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-blue-200/70 to-transparent"
              aria-hidden="true"
            ></span>
          )}
          {isUploading ? (
            <i className="fa-solid fa-spinner fa-spin text-xs relative"></i>
          ) : (
            <i className="fa-solid fa-paper-plane text-xs relative"></i>
          )}
          <span className="relative">{isUploading ? "Importing..." : "Import"}</span>
        </button>
      </div>
    </div>
  );
};

export default FileUpload;